import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'wouter';
import { useProjects } from '../hooks/use-projects';
import { useToast } from '../hooks/use-toast';
import { useQuery } from '@tanstack/react-query';
import { useServices } from '../hooks/use-services';
import StatusDropdown, { ProjectStatus } from '../components/projects/status-dropdown';
import { authInterceptor } from '../lib/auth-interceptor';
import { formatCurrency } from '../lib/utils';
import StatusBadge from '../components/shared/status-badge';
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, isSameMonth, isSameDay, isToday, addMonths, subMonths, addWeeks, subWeeks, isWithinInterval, getDay } from 'date-fns';

// UI Components
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

// Icons
import { 
  Plus, 
  Search, 
  Eye, 
  Edit, 
  Trash2, 
  RefreshCw,
  Building,
  Check,
  Clock,
  DollarSign,
  List,
  Grid,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  MoreHorizontal,
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  BarChart3,
  MessageSquare
} from 'lucide-react';

// Layout Components
import Sidebar from '../components/layout/sidebar';
import MobileSidebar from '../components/layout/mobile-sidebar';
import TopNav from '../components/layout/top-nav';

// Kanban Components
import DraggableKanbanBoard from '../components/projects/draggable-kanban-board';

// Helper: parse project dates consistently (avoids timezone shifting)
const parseProjectDate = (raw: string | Date | null | undefined): Date | null => {
  if (raw == null) return null;
  if (raw instanceof Date) return isNaN(raw.getTime()) ? null : raw;
  const str = String(raw).trim().slice(0, 10);
  const dateOnlyMatch = str.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnlyMatch) {
    const [, y, m, d] = dateOnlyMatch;
    return new Date(Number(y), Number(m) - 1, Number(d), 12, 0, 0);
  }
  const d = new Date(raw);
  return !isNaN(d.getTime()) ? d : null;
};

// Helper function to format date using the normalized parser
const formatDate = (dateString?: string | Date | null) => {
  const date = parseProjectDate(dateString ?? null);
  if (!date) return 'N/A';
  try {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch {
    return 'Invalid Date';
  }
};

// Helper function to get status badge styling (standardized)
const getStatusBadge = (status: string) => {
  return <StatusBadge status={status} type="project" />;
};

export default function ProjectsPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [serviceTypeFilter, setServiceTypeFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>("date");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const VIEW_STORAGE_KEY = 'projects_view_mode';
  const [viewMode, setViewModeState] = useState<'table' | 'cards' | 'kanban' | 'calendar'>(() => {
    if (typeof window === 'undefined') return 'table';
    const saved = sessionStorage.getItem(VIEW_STORAGE_KEY);
    if (saved === 'table' || saved === 'cards' || saved === 'kanban' || saved === 'calendar') return saved;
    return 'table';
  });
  const setViewMode = (mode: 'table' | 'cards' | 'kanban' | 'calendar') => {
    setViewModeState(mode);
    sessionStorage.setItem(VIEW_STORAGE_KEY, mode);
  };
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [calendarView, setCalendarView] = useState<'week' | 'month'>('month');
  const [selectedProjectInCalendar, setSelectedProjectInCalendar] = useState<any | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const { toast } = useToast();
  const { 
    projects = [], 
    isLoadingProjects, 
    projectsError
  } = useProjects();

  // Fetch contractor's custom services for filtering
  const { services: contractorServices } = useServices();

  // Get unique service types for filter from contractor services
  const serviceTypes = Array.from(new Set(contractorServices?.map((s: any) => s.value).filter(Boolean) || []));
  
  // Helper function to get the actual service name from configured services
  const getServiceName = (serviceType: string): string => {
    if (!serviceType) return 'General';
    // Find the service configuration that matches this service type
    const service = contractorServices?.find((s: any) => 
      s.value === serviceType || s.serviceType === serviceType
    );
    // Return the service name (label) if found, otherwise capitalize the type
    return service?.label || service?.name || serviceType.charAt(0).toUpperCase() + serviceType.slice(1);
  };

  // Filter and sort projects
  const filteredProjects = (projects || [])
    .filter((project: any) => {
      const matchesSearch = 
        (project.title || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (project.client?.firstName || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (project.client?.lastName || '').toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesStatus = statusFilter === 'all' || (project.status || '').toLowerCase() === statusFilter.toLowerCase();
      // Fixed: API returns serviceType (camelCase), not service_type (snake_case)
      const matchesServiceType = serviceTypeFilter === 'all' || (project.serviceType || project.service_type || '').toLowerCase() === serviceTypeFilter.toLowerCase();
      
      return matchesSearch && matchesStatus && matchesServiceType;
    })
    .sort((a: any, b: any) => {
      let comparison = 0;
      
      switch (sortBy) {
        case "project":
          const aTitle = (a.title || '').toLowerCase();
          const bTitle = (b.title || '').toLowerCase();
          comparison = aTitle.localeCompare(bTitle);
          break;
        case "client":
          const aClient = `${a.client?.firstName || ''} ${a.client?.lastName || ''}`.toLowerCase();
          const bClient = `${b.client?.firstName || ''} ${b.client?.lastName || ''}`.toLowerCase();
          comparison = aClient.localeCompare(bClient);
          break;
        case "service":
          const aService = (a.serviceType || a.service_type || '').toLowerCase();
          const bService = (b.serviceType || b.service_type || '').toLowerCase();
          comparison = aService.localeCompare(bService);
          break;
        case "status":
          comparison = (a.status || '').localeCompare(b.status || '');
          break;
        case "date":
          comparison = new Date(a.startDate || a.start_date || a.createdAt || a.created_at).getTime() - new Date(b.startDate || b.start_date || b.createdAt || b.created_at).getTime();
          break;
        default:
          comparison = new Date(b.startDate || b.start_date || b.createdAt || b.created_at).getTime() - new Date(a.startDate || a.start_date || a.createdAt || a.created_at).getTime();
      }
      
      return sortDirection === "asc" ? comparison : -comparison;
    });

  // Safe mapping function to prevent errors
  const safeMapProjects = (projects: any[]) => {
    try {
      return projects.map((project: any) => ({
        id: project.id || 0,
        title: project.title || 'Untitled Project',
        description: project.description || '',
        status: project.status || 'pending',
        budget: project.budget || 0,
        startDate: project.startDate || null,
        endDate: project.endDate || null,
        position: 0,
        createdAt: typeof project.createdAt === 'string' ? project.createdAt : new Date().toISOString(),
        updatedAt: typeof project.updatedAt === 'string' ? project.updatedAt : new Date().toISOString(),
        // Use the actual service type coming from the API; avoid hardcoded defaults like "fence"
        serviceType: project.serviceType || project.service_type || '',
        client: project.client ? {
          id: project.client.id || 0,
          firstName: project.client.firstName || '',
          lastName: project.client.lastName || '',
          email: project.client.email || ''
        } : undefined
      }));
    } catch (error) {
      console.error('Error mapping projects:', error);
      return [];
    }
  };

  // Pagination (for table and cards view)
  const totalPages = Math.max(1, Math.ceil(filteredProjects.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const paginatedProjects = filteredProjects.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  // Calculate summary stats
  const totalProjects = filteredProjects.length;
  const completedProjects = filteredProjects.filter((p: any) => (p.status || '').toLowerCase() === 'completed').length;
  const inProgressProjects = filteredProjects.filter((p: any) => (p.status || '').toLowerCase() === 'in_progress').length;
  const totalBudget = filteredProjects.reduce((sum: number, project: any) => sum + (parseFloat(project.budget) || 0), 0);

  // Handle refresh
  const handleRefresh = () => {
    toast({
      title: 'Data refreshed',
      description: 'Project data has been refreshed.',
    });
  };

  // Handle sort
  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortDirection("desc");
    }
  };

  // Get sort icon
  const getSortIcon = (column: string) => {
    if (sortBy !== column) {
      return <ChevronsUpDown className="h-4 w-4 text-slate-400" />;
    }
    return sortDirection === "asc" 
      ? <ChevronUp className="h-4 w-4 text-green-500" />
      : <ChevronDown className="h-4 w-4 text-green-500" />;
  };

  // Calendar Project: pastel card colors and date helpers
  const PROJECT_CARD_COLORS = [
    'bg-violet-200/90 text-violet-900 border-violet-300',
    'bg-blue-200/90 text-blue-900 border-blue-300',
    'bg-emerald-200/90 text-emerald-900 border-emerald-300',
    'bg-amber-200/90 text-amber-900 border-amber-300',
    'bg-rose-200/90 text-rose-900 border-rose-300',
    'bg-cyan-200/90 text-cyan-900 border-cyan-300',
    'bg-fuchsia-200/90 text-fuchsia-900 border-fuchsia-300',
    'bg-teal-200/90 text-teal-900 border-teal-300',
  ];
  const getProjectRange = (project: any): { start: Date; end: Date } | null => {
    const rawStart = project.startDate ?? project.start_date;
    if (!rawStart) return null;
    const start = new Date(rawStart);
    if (isNaN(start.getTime())) return null;
    const rawEnd = project.endDate ?? project.end_date;
    const end = rawEnd ? new Date(rawEnd) : start;
    return { start, end: isNaN(end.getTime()) ? start : end };
  };
  const projectsWithDates = useMemo(() => 
    filteredProjects.filter((p: any) => getProjectRange(p) !== null),
    [filteredProjects]
  );
  const getProjectsForDay = (day: Date) =>
    projectsWithDates.filter((p: any) => {
      const range = getProjectRange(p);
      if (!range) return false;
      return isWithinInterval(day, { start: range.start, end: range.end });
    });

  const calendarDays = useMemo(() => {
    if (calendarView === 'week') {
      return eachDayOfInterval({ start: startOfWeek(calendarDate), end: endOfWeek(calendarDate) });
    }
    const monthStart = startOfMonth(calendarDate);
    const daysInMonth = eachDayOfInterval({ start: monthStart, end: endOfMonth(calendarDate) });
    const startDay = getDay(monthStart);
    return [...Array(startDay).fill(null), ...daysInMonth];
  }, [calendarDate, calendarView]);

  // Handle delete click
  const handleDeleteClick = (project: any) => {
    toast({
      title: 'Delete Project',
      description: `Delete functionality for ${project.title} would be implemented here.`,
    });
  };

  // Handle status change
  const handleStatusChange = async (projectId: number, newStatus: ProjectStatus) => {
    try {
      console.log('Updating project status:', { projectId, newStatus });
      
      const response = await fetch(`/api/protected/projects?id=${projectId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...authInterceptor.addAuthHeaders(),
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('Status update result:', result);
      
      // Refresh projects data to reflect the change
      window.location.reload();
      
      toast({
        title: 'Status Updated',
        description: `Project status changed to ${newStatus}`,
      });
    } catch (error) {
      console.error('Error updating project status:', error);
      toast({
        title: 'Error',
        description: 'Failed to update project status',
        variant: 'destructive',
      });
    }
  };

  if (projectsError) {
    return (
      <div className="remodra-layout">
        <div className="hidden md:block"><Sidebar /></div>
        <MobileSidebar />
        <div className="remodra-main-mobile md:ml-64">
          <TopNav />
          <div className="remodra-content">
          <main className="dashboard-2025 bg-[var(--dash-bg)] min-h-screen p-6 md:p-8 pt-24 flex flex-col items-center">
              <div className="w-full">
                <h1 className="text-2xl font-bold text-[var(--dash-text)]">Projects</h1>
                <p className="text-sm text-[var(--dash-text-muted)] mt-0.5">Manage your construction projects and track progress.</p>
                <div className="mt-6 rounded-xl border border-[var(--dash-border)] bg-[var(--dash-card)] p-8 text-center">
                  <div className="text-4xl mb-4">⚠️</div>
                  <div className="text-lg font-medium text-[var(--dash-text)]">Error loading projects</div>
                  <p className="text-[var(--dash-text-muted)] mt-2">Please try refreshing the page.</p>
                  <Button className="mt-4" onClick={handleRefresh}>
                    <RefreshCw className="h-4 w-4 mr-2" />Refresh
                  </Button>
                </div>
              </div>
            </main>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="remodra-layout">
      <div className="hidden md:block">
        <Sidebar />
      </div>
      <MobileSidebar />
      <div className="remodra-main-mobile md:ml-64">
        <TopNav />
        <div className="remodra-content">
          <main className="dashboard-2025 bg-[var(--dash-bg)] min-h-screen p-4 md:p-6 pt-24 flex flex-col items-center">
            <div className="w-full space-y-3">
              <div>
                <h1 className="text-2xl font-bold text-[var(--dash-text)]">Projects</h1>
                <p className="text-sm text-[var(--dash-text-muted)] mt-0.5">Manage your construction projects and track progress.</p>
              </div>
              <div className="flex gap-1 border-b border-[var(--dash-border)]">
                <button type="button" onClick={() => setViewMode('table')} className={`px-4 py-2.5 text-sm font-medium rounded-t-lg ${viewMode === 'table' ? 'dash-tab-active' : 'dash-tab-inactive hover:text-[var(--dash-text)]'}`}>Project list</button>
                <button type="button" onClick={() => setViewMode('kanban')} className={`px-4 py-2.5 text-sm rounded-t-lg ${viewMode === 'kanban' ? 'dash-tab-active' : 'dash-tab-inactive hover:text-[var(--dash-text)]'}`}>Board</button>
                <button type="button" onClick={() => setViewMode('cards')} className={`px-4 py-2.5 text-sm rounded-t-lg ${viewMode === 'cards' ? 'dash-tab-active' : 'dash-tab-inactive hover:text-[var(--dash-text)]'}`}>Cards</button>
                <button type="button" onClick={() => setViewMode('calendar')} className={`px-4 py-2.5 text-sm rounded-t-lg ${viewMode === 'calendar' ? 'dash-tab-active' : 'dash-tab-inactive hover:text-[var(--dash-text)]'}`}><CalendarIcon className="h-4 w-4 inline-block mr-1.5" />Calendar Project</button>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-[200px] max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--dash-text-muted)]" />
                  <Input
                    type="text"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                    className="pl-9 h-10 rounded-lg border-[var(--dash-border)] bg-[var(--dash-card)] text-[var(--dash-text)] placeholder:text-[var(--dash-text-muted)]"
                  />
                </div>
                <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
                  <SelectTrigger className="w-[160px] h-10 rounded-lg border-[var(--dash-border)] bg-[var(--dash-card)]">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All status</SelectItem>
                    <SelectItem value="project_initiated">Project Initiated</SelectItem>
                    <SelectItem value="site_assessment">Site Assessment</SelectItem>
                    <SelectItem value="permits_approvals">Permits & Approvals</SelectItem>
                    <SelectItem value="materials_ordered">Materials Ordered</SelectItem>
                    <SelectItem value="installation_begins">Installation Begins</SelectItem>
                    <SelectItem value="quality_inspection">Quality Inspection</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={serviceTypeFilter} onValueChange={(v) => { setServiceTypeFilter(v); setPage(1); }}>
                  <SelectTrigger className="w-[140px] h-10 rounded-lg border-[var(--dash-border)] bg-[var(--dash-card)]">
                    <SelectValue placeholder="Service" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All services</SelectItem>
                    {serviceTypes.length === 0 ? <SelectItem value="no-services" disabled>No services</SelectItem> : serviceTypes.map((st) => <SelectItem key={st} value={st}>{getServiceName(st)}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button asChild className="h-10 rounded-lg bg-[var(--dash-accent)] hover:opacity-90 text-white ml-auto">
                  <Link href="/projects/create"><Plus className="h-4 w-4 mr-2" />Create project</Link>
                </Button>
                <Button variant="outline" size="sm" className="h-10 rounded-lg border-[var(--dash-border)]" onClick={handleRefresh}>
                  <RefreshCw className="h-4 w-4 mr-2" />Refresh
                </Button>
              </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="dash-stat-card">
                  <div className="flex items-center gap-2 mb-1"><Building className="h-5 w-5 text-[var(--dash-text-muted)]" /></div>
                  <div className="flex items-baseline">
                    <span className="dash-stat-number">{totalProjects}</span>
                    <span className="dash-stat-delta positive">+{(projects || []).filter((p: any) => new Date(p.createdAt || p.created_at || 0).getMonth() === new Date().getMonth()).length}</span>
                  </div>
                  <div className="dash-stat-label">Total projects</div>
                </div>
                <div className="dash-stat-card">
                  <div className="flex items-center gap-2 mb-1"><Check className="h-5 w-5 text-[var(--dash-text-muted)]" /></div>
                  <span className="dash-stat-number">{completedProjects}</span>
                  <div className="dash-stat-label">Completed</div>
                </div>
                <div className="dash-stat-card">
                  <div className="flex items-center gap-2 mb-1"><Clock className="h-5 w-5 text-[var(--dash-text-muted)]" /></div>
                  <span className="dash-stat-number">{inProgressProjects}</span>
                  <div className="dash-stat-label">In progress</div>
                </div>
                <div className="dash-stat-card">
                  <div className="flex items-center gap-2 mb-1"><DollarSign className="h-5 w-5 text-[var(--dash-text-muted)]" /></div>
                  <span className="dash-stat-number">{formatCurrency(totalBudget)}</span>
                  <div className="dash-stat-label">Total budget</div>
                </div>
              </div>


            {/* Projects Display */}
            <div className="rounded-xl border border-[var(--dash-border)] bg-[var(--dash-card)] shadow-[var(--dash-shadow)]">
            {isLoadingProjects ? (
              <div className="text-center py-12 text-[var(--dash-text-muted)]">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-[var(--dash-border)] border-t-[var(--dash-accent)] mx-auto"></div>
                <p className="mt-2">Loading projects…</p>
              </div>
            ) : filteredProjects.length === 0 && viewMode !== 'calendar' ? (
              <div className="text-center py-12 text-[var(--dash-text-muted)]">
                <div className="text-4xl mb-4">🏗️</div>
                <div className="text-lg font-medium text-[var(--dash-text)]">No projects found</div>
                <p className="mt-2">
                  {searchQuery || statusFilter !== 'all' ? 'No projects match your filters.' : 'Start by creating your first project.'}
                </p>
                <Button className="mt-4" asChild>
                  <Link href="/projects/create">
                    <Plus className="h-4 w-4 mr-2" />
                    Create first project
                  </Link>
                </Button>
              </div>
            ) : viewMode === 'calendar' ? (
              <div className="flex flex-col lg:flex-row gap-4 p-4 min-h-[500px]">
                {/* Left: Calendar */}
                <div className="flex-1 min-w-0 rounded-xl border border-[var(--dash-border)] bg-[var(--dash-card)] shadow-[var(--dash-shadow)] overflow-hidden">
                  <div className="flex flex-wrap items-center justify-between gap-2 p-3 border-b border-[var(--dash-border)] bg-[var(--dash-bg)]">
                    <div className="flex items-center gap-2">
                      <button type="button" onClick={() => setCalendarView('week')} className={`px-3 py-1.5 text-sm font-medium rounded-lg ${calendarView === 'week' ? 'dash-tab-active' : 'dash-tab-inactive'}`}>Weekly</button>
                      <button type="button" onClick={() => setCalendarView('month')} className={`px-3 py-1.5 text-sm font-medium rounded-lg ${calendarView === 'month' ? 'dash-tab-active' : 'dash-tab-inactive'}`}>Monthly</button>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="outline" size="sm" className="h-9 w-9 p-0 border-[var(--dash-border)]" onClick={() => setCalendarDate(calendarView === 'week' ? subWeeks(calendarDate, 1) : subMonths(calendarDate, 1))}><ChevronLeft className="h-4 w-4" /></Button>
                      <span className="text-xs sm:text-sm font-medium text-[var(--dash-text)] min-w-0 text-center truncate max-w-[140px] sm:max-w-none">
                        {calendarView === 'week' ? `${format(startOfWeek(calendarDate), 'MMM d')} – ${format(endOfWeek(calendarDate), 'MMM d')}` : format(calendarDate, 'MMMM yyyy')}
                      </span>
                      <Button variant="outline" size="sm" className="h-9 w-9 p-0 border-[var(--dash-border)]" onClick={() => setCalendarDate(calendarView === 'week' ? addWeeks(calendarDate, 1) : addMonths(calendarDate, 1))}><ChevronRight className="h-4 w-4" /></Button>
                    </div>
                  </div>
                  <div className="p-3 overflow-auto min-w-0">
                    {calendarView === 'week' ? (
                      <div className="grid grid-cols-7 gap-2 min-w-[280px] sm:min-w-0">
                        {calendarDays.map((day) => (
                          <div key={day.toISOString()} className="min-h-[100px] rounded-lg border border-[var(--dash-border)] p-2 bg-[var(--dash-bg)]">
                            <div className={`text-xs font-medium mb-1.5 ${isToday(day) ? 'text-[var(--dash-accent)]' : 'text-[var(--dash-text-muted)]'}`}>{format(day, 'EEE')}</div>
                            <div className={`text-sm font-semibold mb-2 ${isToday(day) ? 'bg-[var(--dash-accent)] text-white w-7 h-7 rounded flex items-center justify-center' : 'text-[var(--dash-text)]'}`}>{format(day, 'd')}</div>
                            <div className="space-y-1">
                              {getProjectsForDay(day).map((project: any, idx: number) => (
                                <button
                                  key={project.id}
                                  type="button"
                                  onClick={() => setSelectedProjectInCalendar(project)}
                                  className="w-full text-left"
                                >
                                  <div className={`text-xs px-2 py-1.5 rounded-md border truncate cursor-pointer hover:opacity-90 ${PROJECT_CARD_COLORS[(project.id || idx) % PROJECT_CARD_COLORS.length]}`} title={project.title}>
                                    P-{String(project.id).padStart(2, '0')} {project.title || 'Untitled'}
                                  </div>
                                </button>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="grid grid-cols-7 gap-1 min-w-[280px] sm:min-w-0">
                        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
                          <div key={d} className="text-center text-xs font-medium text-[var(--dash-text-muted)] py-1">{d}</div>
                        ))}
                        {calendarDays.map((day, index) => {
                          if (!day) return <div key={`empty-${index}`} className="min-h-[80px] p-1 border border-transparent" />;
                          const isCurrentMonth = isSameMonth(day, calendarDate);
                          const dayProjects = getProjectsForDay(day);
                          return (
                            <div key={day.toISOString()} className={`min-h-[80px] rounded border p-1 ${isCurrentMonth ? 'bg-[var(--dash-card)] border-[var(--dash-border)]' : 'bg-[var(--dash-bg)] border-transparent text-[var(--dash-text-muted)]'}`}>
                              <span className={`text-xs ${isToday(day) ? 'bg-[var(--dash-accent)] text-white rounded-full w-5 h-5 inline-flex items-center justify-center' : ''}`}>{format(day, 'd')}</span>
                              <div className="space-y-0.5 mt-0.5">
                                {dayProjects.slice(0, 3).map((project: any, idx: number) => (
                                  <button
                                    key={project.id}
                                    type="button"
                                    onClick={() => setSelectedProjectInCalendar(project)}
                                    className="w-full text-left"
                                  >
                                    <div className={`text-[10px] px-1 py-0.5 rounded border truncate cursor-pointer hover:opacity-90 ${PROJECT_CARD_COLORS[(project.id || idx) % PROJECT_CARD_COLORS.length]}`} title={project.title}>{project.title?.slice(0, 12) || 'P-' + project.id}</div>
                                  </button>
                                ))}
                                {dayProjects.length > 3 && <span className="text-[10px] text-[var(--dash-text-muted)]">+{dayProjects.length - 3}</span>}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
                {/* Right: Project insights sidebar */}
                <div className="w-full lg:w-80 shrink-0 rounded-xl border border-[var(--dash-border)] bg-[var(--dash-card)] shadow-[var(--dash-shadow)] overflow-hidden flex flex-col">
                  <div className="p-3 border-b border-[var(--dash-border)] flex items-center justify-between">
                    <span className="font-medium text-[var(--dash-text)] flex items-center gap-2"><MessageSquare className="h-4 w-4" />Project insights</span>
                    {selectedProjectInCalendar && (
                      <Button variant="ghost" size="sm" className="h-8 px-2 text-[var(--dash-text-muted)]" onClick={() => setSelectedProjectInCalendar(null)}>Back to summary</Button>
                    )}
                  </div>
                  <div className="p-3 flex-1 overflow-auto space-y-4">
                    {selectedProjectInCalendar ? (
                      <>
                        <div>
                          <h3 className="font-semibold text-[var(--dash-text)]">{selectedProjectInCalendar.title || 'Untitled project'}</h3>
                          <p className="text-xs text-[var(--dash-text-muted)] mt-0.5">Created {formatDate(selectedProjectInCalendar.createdAt || selectedProjectInCalendar.created_at)}</p>
                        </div>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between"><span className="text-[var(--dash-text-muted)]">Status</span>{getStatusBadge(selectedProjectInCalendar.status || 'pending')}</div>
                          <div className="flex justify-between"><span className="text-[var(--dash-text-muted)]">Service</span><span className="text-[var(--dash-text)]">{getServiceName(selectedProjectInCalendar.serviceType || selectedProjectInCalendar.service_type || '')}</span></div>
                          <div className="flex justify-between"><span className="text-[var(--dash-text-muted)]">Timeline</span><span className="text-[var(--dash-text)]">{formatDate(selectedProjectInCalendar.startDate || selectedProjectInCalendar.start_date)} – {formatDate(selectedProjectInCalendar.endDate || selectedProjectInCalendar.end_date)}</span></div>
                          <div className="flex justify-between"><span className="text-[var(--dash-text-muted)]">Budget</span><span className="text-[var(--dash-text)]">{formatCurrency(selectedProjectInCalendar.budget ?? selectedProjectInCalendar.estimatedBudget ?? 0)}</span></div>
                          {(selectedProjectInCalendar.clientName || selectedProjectInCalendar.client_name) && (
                            <div className="flex justify-between"><span className="text-[var(--dash-text-muted)]">Client</span><span className="text-[var(--dash-text)]">{selectedProjectInCalendar.clientName || selectedProjectInCalendar.client_name}</span></div>
                          )}
                        </div>
                        {(selectedProjectInCalendar.scope || selectedProjectInCalendar.summary || selectedProjectInCalendar.description) && (
                          <div className="rounded-lg border border-[var(--dash-border)] p-3">
                            <h4 className="text-xs font-medium text-[var(--dash-text-muted)] mb-1.5">Description</h4>
                            <p className="text-xs text-[var(--dash-text)] line-clamp-4">{(selectedProjectInCalendar.scope || selectedProjectInCalendar.summary || selectedProjectInCalendar.description || '').slice(0, 300)}{((selectedProjectInCalendar.scope || selectedProjectInCalendar.summary || selectedProjectInCalendar.description || '').length > 300 ? '…' : '')}</p>
                          </div>
                        )}
                        <Button asChild className="w-full" size="sm">
                          <Link href={`/projects/${selectedProjectInCalendar.id}`}><Eye className="h-4 w-4 mr-2" />View full project</Link>
                        </Button>
                      </>
                    ) : (
                      <>
                        <p className="text-sm text-[var(--dash-text-muted)]">Projects on the calendar this {calendarView === 'week' ? 'week' : 'month'}, by start and end dates. Click a project to see its details here.</p>
                        <div className="grid grid-cols-2 gap-2">
                          <div className="rounded-lg border border-[var(--dash-border)] p-3 text-center">
                            <div className="text-xl font-semibold text-[var(--dash-text)]">{projectsWithDates.length}</div>
                            <div className="text-xs text-[var(--dash-text-muted)]">With dates</div>
                          </div>
                          <div className="rounded-lg border border-[var(--dash-border)] p-3 text-center">
                            <div className="text-xl font-semibold text-[var(--dash-text)]">{filteredProjects.length - projectsWithDates.length}</div>
                            <div className="text-xs text-[var(--dash-text-muted)]">No dates</div>
                          </div>
                          <div className="rounded-lg border border-[var(--dash-border)] p-3 text-center col-span-2">
                            <div className="text-lg font-semibold text-[var(--dash-text)]">{formatCurrency(totalBudget)}</div>
                            <div className="text-xs text-[var(--dash-text-muted)]">Total budget (filtered)</div>
                          </div>
                        </div>
                        <div>
                          <h4 className="text-sm font-medium text-[var(--dash-text)] mb-2 flex items-center gap-1.5"><BarChart3 className="h-4 w-4" />Performance overview</h4>
                          <p className="text-xs text-[var(--dash-text-muted)] mb-2">Project count by status this month.</p>
                          <div className="space-y-1.5">
                            <div className="flex justify-between text-xs"><span className="text-[var(--dash-text-muted)]">Completed</span><span className="font-medium text-[var(--dash-text)]">{completedProjects}</span></div>
                            <div className="flex justify-between text-xs"><span className="text-[var(--dash-text-muted)]">In progress</span><span className="font-medium text-[var(--dash-text)]">{inProgressProjects}</span></div>
                            <div className="flex justify-between text-xs"><span className="text-[var(--dash-text-muted)]">Other</span><span className="font-medium text-[var(--dash-text)]">{totalProjects - completedProjects - inProgressProjects}</span></div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ) : viewMode === 'table' ? (
              <>
                  <div className="hidden lg:block overflow-x-auto">
                    <Table>
                    <TableHeader>
                      <TableRow className="border-[var(--dash-border)] hover:bg-transparent">
                        <TableHead className="text-[var(--dash-text-muted)] font-medium cursor-pointer hover:bg-slate-50/80" onClick={() => handleSort("project")}><div className="flex items-center gap-1">Project {getSortIcon("project")}</div></TableHead>
                        <TableHead className="text-[var(--dash-text-muted)] font-medium cursor-pointer hover:bg-slate-50/80" onClick={() => handleSort("client")}><div className="flex items-center gap-1">Client {getSortIcon("client")}</div></TableHead>
                        <TableHead className="text-[var(--dash-text-muted)] font-medium cursor-pointer hover:bg-slate-50/80" onClick={() => handleSort("service")}><div className="flex items-center gap-1">Service {getSortIcon("service")}</div></TableHead>
                        <TableHead className="text-[var(--dash-text-muted)] font-medium cursor-pointer hover:bg-slate-50/80" onClick={() => handleSort("status")}><div className="flex items-center gap-1">Status {getSortIcon("status")}</div></TableHead>
                        <TableHead className="text-[var(--dash-text-muted)] font-medium">Budget</TableHead>
                        <TableHead className="text-[var(--dash-text-muted)] font-medium cursor-pointer hover:bg-slate-50/80" onClick={() => handleSort("date")}><div className="flex items-center gap-1">Start date {getSortIcon("date")}</div></TableHead>
                        <TableHead className="text-[var(--dash-text-muted)] font-medium">End date</TableHead>
                        <TableHead className="text-[var(--dash-text-muted)] font-medium text-right w-24">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginatedProjects.map((project: any) => (
                        <TableRow key={project.id} className="border-[var(--dash-border)] hover:bg-slate-50/80">
                          <TableCell className="font-medium py-2">
                            <Link href={`/projects/${project.id}`} className="text-[var(--dash-text)] hover:underline">{project.title || 'Untitled Project'}</Link>
                          </TableCell>
                          <TableCell className="py-2 text-[var(--dash-text)]">{project.client ? `${project.client.firstName || ''} ${project.client.lastName || ''}`.trim() || 'N/A' : 'N/A'}</TableCell>
                          <TableCell className="py-2 text-[var(--dash-text)]">{getServiceName(project.service_type || project.serviceType)}</TableCell>
                          <TableCell className="py-2">
                            <StatusDropdown currentStatus={project.status} onStatusChange={(newStatus) => handleStatusChange(project.id, newStatus)} size="sm" />
                          </TableCell>
                          <TableCell className="font-medium py-2 text-[var(--dash-text)]">{formatCurrency(project.budget)}</TableCell>
                          <TableCell className="py-2 text-[var(--dash-text)]">{formatDate(project.startDate || project.start_date)}</TableCell>
                          <TableCell className="py-2 text-[var(--dash-text)]">{formatDate(project.endDate || project.end_date)}</TableCell>
                          <TableCell className="py-2 text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0 border border-[var(--dash-border)] rounded-md"><MoreHorizontal className="h-4 w-4" /></Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="border-[var(--dash-border)] bg-[var(--dash-card)]">
                                <DropdownMenuItem asChild><Link href={`/projects/${project.id}`}><Eye className="h-4 w-4 mr-2" />View</Link></DropdownMenuItem>
                                <DropdownMenuItem asChild><Link href={`/projects/${project.id}/edit`}><Edit className="h-4 w-4 mr-2" />Edit</Link></DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => handleDeleteClick(project)} className="text-red-600"><Trash2 className="h-4 w-4 mr-2" />Delete</DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                    </Table>
                  </div>

                  {/* Mobile: Card Layout */}
                  <div className="lg:hidden space-y-4 p-4">
                    {paginatedProjects.map((project: any) => (
                      <div key={project.id} className="rounded-lg border border-[var(--dash-border)] bg-[var(--dash-card)] p-4 space-y-3">
                        <div className="flex items-start justify-between">
                          <div>
                            <Link href={`/projects/${project.id}`} className="font-medium text-[var(--dash-text)] hover:underline">{project.title || 'Untitled Project'}</Link>
                            <p className="text-sm text-[var(--dash-text-muted)] mt-0.5">{project.client ? `${project.client.firstName || ''} ${project.client.lastName || ''}`.trim() || 'N/A' : 'N/A'}</p>
                          </div>
                          <StatusDropdown currentStatus={project.status} onStatusChange={(newStatus) => handleStatusChange(project.id, newStatus)} size="sm" />
                        </div>
                        <div className="grid grid-cols-2 gap-2 pt-2 border-t border-[var(--dash-border)] text-sm">
                          <div><span className="text-[var(--dash-text-muted)]">Service</span><br /><span className="text-[var(--dash-text)]">{getServiceName(project.service_type || project.serviceType)}</span></div>
                          <div><span className="text-[var(--dash-text-muted)]">Budget</span><br /><span className="font-medium text-[var(--dash-text)]">{formatCurrency(project.budget)}</span></div>
                          <div><span className="text-[var(--dash-text-muted)]">Start</span><br /><span className="text-[var(--dash-text)]">{formatDate(project.startDate || project.start_date)}</span></div>
                          <div><span className="text-[var(--dash-text-muted)]">End</span><br /><span className="text-[var(--dash-text)]">{formatDate(project.endDate || project.end_date)}</span></div>
                        </div>
                        <div className="pt-2 flex justify-end">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild><Button variant="ghost" size="sm" className="h-8 border border-[var(--dash-border)]"><MoreHorizontal className="h-4 w-4 mr-1" />Actions</Button></DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="border-[var(--dash-border)]">
                              <DropdownMenuItem asChild><Link href={`/projects/${project.id}`}><Eye className="h-4 w-4 mr-2" />View</Link></DropdownMenuItem>
                              <DropdownMenuItem asChild><Link href={`/projects/${project.id}/edit`}><Edit className="h-4 w-4 mr-2" />Edit</Link></DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => handleDeleteClick(project)} className="text-red-600"><Trash2 className="h-4 w-4 mr-2" />Delete</DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    ))}
                  </div>
                  {totalPages > 0 && (
                    <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-2 border-t border-[var(--dash-border)] bg-[var(--dash-bg)]">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-[var(--dash-text-muted)]">Rows per page</span>
                        <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}>
                          <SelectTrigger className="w-[72px] h-9 border-[var(--dash-border)]"><SelectValue /></SelectTrigger>
                          <SelectContent>{[10, 25, 50, 100].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <span className="text-sm text-[var(--dash-text-muted)]">{(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, filteredProjects.length)} of {filteredProjects.length}</span>
                      <div className="flex items-center gap-1">
                        <Button variant="outline" size="sm" className="h-9 w-9 p-0 border-[var(--dash-border)]" disabled={currentPage <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>←</Button>
                        {(() => {
                          const show = Math.min(5, totalPages);
                          let start = currentPage <= 2 ? 1 : Math.min(currentPage - 2, totalPages - show + 1);
                          start = Math.max(1, start);
                          const pages: number[] = [];
                          for (let i = 0; i < show; i++) pages.push(start + i);
                          return pages.map((p) => (
                            <Button key={p} variant={currentPage === p ? 'default' : 'outline'} size="sm" className="h-9 w-9 p-0 min-w-9" onClick={() => setPage(p)}>{p}</Button>
                          ));
                        })()}
                        <Button variant="outline" size="sm" className="h-9 w-9 p-0 border-[var(--dash-border)]" disabled={currentPage >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>→</Button>
                      </div>
                    </div>
                  )}
                </>
            ) : viewMode === 'kanban' ? (
              <DraggableKanbanBoard
                projects={safeMapProjects(filteredProjects)}
                onViewProject={(project) => window.location.href = `/projects/${project.id}`}
                onEditProject={(project) => window.location.href = `/projects/${project.id}/edit`}
                onDeleteProject={(projectId) => handleDeleteClick({ id: projectId, title: 'Project' })}
                selectedServiceType={serviceTypeFilter !== 'all' ? serviceTypeFilter : 'fence'}
              />
            ) : (
              <Card className="bg-slate-800 border-slate-600">
                <CardHeader>
                  <CardTitle className="text-green-500">Project Cards</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredProjects.map((project: any) => (
                      <Card key={project.id} className="bg-slate-700 border-slate-600 hover:shadow-lg transition-all duration-300">
                        <CardHeader>
                          <div className="flex items-center justify-between">
                            <div>
                              <CardTitle className="text-lg">
                                <Link href={`/projects/${project.id}`} className="text-green-500 hover:underline">
                                  {project.title || 'Untitled Project'}
                                </Link>
                              </CardTitle>
                              <p className="text-sm text-slate-400">
                                {project.client ? `${project.client.firstName || ''} ${project.client.lastName || ''}`.trim() || 'N/A' : 'N/A'}
                              </p>
                            </div>
                            {getStatusBadge(project.status)}
                          </div>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-slate-400">Service Name</span>
                              <span className="text-sm font-medium text-slate-800 dark:text-slate-200">
                                {getServiceName(project.service_type || project.serviceType)}
                              </span>
                            </div>
                            <div>
                              <p className="text-sm text-slate-400">Budget</p>
                              <p className="text-xl font-bold text-green-400">{formatCurrency(project.budget)}</p>
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-sm">
                              <div>
                                <p className="text-slate-400">Start</p>
                                <p className="font-medium">{formatDate(project.startDate || project.start_date)}</p>
                              </div>
                              <div>
                                <p className="text-slate-400">End</p>
                                <p className="font-medium">{formatDate(project.endDate || project.end_date)}</p>
                              </div>
                            </div>
                            <div className="flex items-center justify-end pt-2">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 !h-8 !min-h-0 w-8 h-8 p-0 min-h-[44px] md:min-h-0"
                                    title="Actions"
                                  >
                                    <ChevronDown className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent className="bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600" align="end">
                                  <DropdownMenuItem 
                                    asChild
                                    className="text-slate-900 dark:text-slate-100 hover:bg-slate-200 dark:hover:bg-slate-600 cursor-pointer"
                                  >
                                    <Link href={`/projects/${project.id}`}>
                                      <Eye className="h-4 w-4 mr-2" />
                                      <span>View Details</span>
                                    </Link>
                                  </DropdownMenuItem>
                                  <DropdownMenuItem 
                                    asChild
                                    className="text-slate-900 dark:text-slate-100 hover:bg-slate-200 dark:hover:bg-slate-600 cursor-pointer"
                                  >
                                    <Link href={`/projects/${project.id}/edit`}>
                                      <Edit className="h-4 w-4 mr-2" />
                                      <span>Edit</span>
                                    </Link>
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator className="bg-slate-200 dark:bg-slate-600" />
                                  <DropdownMenuItem 
                                    onClick={() => handleDeleteClick(project)}
                                    className="text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 cursor-pointer"
                                  >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    <span>Delete</span>
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
            </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
} 